import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import type { IWorkJourneyManagementUser } from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';

import { groupAttendanceByJourney, toUserStatusInput } from './attendanceMetrics';
import {
  isUserAbsent,
  isUserPresent,
  resolveUserRowAttendanceState,
} from './attendanceStatus/resolveAttendanceStatus';
import type { AttendanceStatusDisplay } from './attendanceStatus/types';

export interface WorkJourneyReplacementTarget {
  /** Attendance dashboard / Firestore record id — unique per journey day row. */
  attendanceRecordId: string;
  user: IWorkJourneyManagementUser;
  absentUsers: IWorkJourneyManagementUser[];
  workJourneyConfigId: string;
  coverDate: string;
  journeyName: string;
  currentMembers: number;
  requiredMembers: number;
  status: AttendanceStatusDisplay;
}

const toManagementUser = (item: AttendanceDashboardItem): IWorkJourneyManagementUser => ({
  id: item.employeeId,
  displayName: item.userDisplayName,
  firstName: item.userFirstName ?? '',
  lastName: item.userLastName ?? '',
  idCard: item.userIdCard ?? '',
  email: '',
});

/**
 * Builds replace-worker candidates for Real Time.
 * Includes every scheduled journey member (attended, late, not started, or absent)
 * so operators can cover both no-shows and early leavers.
 */
export const buildWorkJourneyReplacementTargets = (
  filteredItems: AttendanceDashboardItem[],
  allItems: AttendanceDashboardItem[],
  fallbackDateMs = Date.now(),
): WorkJourneyReplacementTarget[] => {
  const groupsByRecordId = new Map<string, AttendanceDashboardItem[]>();
  groupAttendanceByJourney(allItems).forEach(group => {
    group.records.forEach(record => {
      groupsByRecordId.set(record.id, group.records);
    });
  });

  return filteredItems.flatMap(item => {
    if (!item.workJourneyConfigId || DateUtil.getTimestampMs(item.scheduledStartAt) === null) {
      return [];
    }

    const selectedUser = toManagementUser(item);
    const journeyRecords = groupsByRecordId.get(item.id) ?? [item];
    const journeyAbsentees = journeyRecords
      .filter(record => isUserAbsent(toUserStatusInput(record)))
      .map(toManagementUser);
    // Keep multi-absent cover context when the selected user is already absent;
    // otherwise replace only that member (e.g. early leaver who already attended).
    const absentUsers = journeyAbsentees.some(user => user.id === selectedUser.id)
      ? journeyAbsentees
      : [selectedUser];
    const currentMembers = journeyRecords.filter(record =>
      isUserPresent(toUserStatusInput(record)),
    ).length;
    const coverDate = DateUtil.toDateString(
      DateUtil.getTimestampMs(item.scheduledStartAt) ?? fallbackDateMs,
      'YYYY-MM-DD',
      DateUtil.getDefaultTimeZone(),
    );

    return [
      {
        attendanceRecordId: item.id,
        user: selectedUser,
        absentUsers,
        workJourneyConfigId: item.workJourneyConfigId,
        coverDate,
        journeyName: item.journeyName,
        currentMembers,
        requiredMembers: item.requiredMembers,
        status: resolveUserRowAttendanceState(toUserStatusInput(item)),
      },
    ];
  });
};
